import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Shield, Eye, Database, UserPlus, Download } from "lucide-react";
import { ContactPermission } from "@/hooks/useDeviceContacts";
import { useTranslation } from "react-i18next";
import { Capacitor } from "@capacitor/core";
import elefaMascot from "@/assets/elefa-app-icon.webp";

interface ContactPermissionSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onAllow: () => void;
  permission: ContactPermission;
  onOpenSettings?: () => void;
  isSupported?: boolean;
  isRequesting?: boolean;
}

export function ContactPermissionSheet({
  isOpen,
  onClose,
  onAllow,
  permission,
  onOpenSettings,
  isSupported = true,
  isRequesting = false,
}: ContactPermissionSheetProps) {
  const { t } = useTranslation("common");
  const isDenied = permission === "denied";
  const isNative = Capacitor.isNativePlatform();

  // Platform detection for messaging
  const showNativeFlow = isSupported && (isNative || isDenied);
  const showWebPickerFlow = isSupported && !isNative && !isDenied;
  const showManualFlow = !isSupported;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="bottom" className="rounded-t-3xl pb-10">
        <SheetHeader className="pb-2">
          <SheetTitle className="text-center text-xl">
            {showManualFlow
              ? t("manageYourContacts", { defaultValue: "Manage Your Contacts" })
              : t("findFriendsOnElefa")}
          </SheetTitle>
        </SheetHeader>

        <div className="flex flex-col items-center text-center px-4 space-y-5">
          <img
            src={elefaMascot}
            alt="elefa"
            className="w-20 h-20 object-contain"
          />

          <p className="text-muted-foreground text-sm max-w-xs">
            {showManualFlow
              ? t("addContactsManuallyPrompt", {
                  defaultValue:
                    "Add contacts manually or download the elefa app on your phone to sync your phonebook automatically.",
                })
              : isDenied
                ? t("contactPermissionDenied")
                : showWebPickerFlow
                  ? t("selectContactsFromPhone", {
                      defaultValue:
                        "Select contacts from your phone to quickly call and discover friends on elefa.",
                    })
                  : t("contactPermissionPrompt")}
          </p>

          {/* Privacy assurances - show for native/web picker flows */}
          {!showManualFlow && (
            <div className="space-y-3 w-full max-w-xs">
              <div className="flex items-start gap-3 text-left">
                <Shield className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <span className="text-sm text-foreground">
                  {t("contactsNeverLeaveDevice")}
                </span>
              </div>
              <div className="flex items-start gap-3 text-left">
                <Eye className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <span className="text-sm text-foreground">
                  {t("phoneNumbersHashed")}
                </span>
              </div>
              <div className="flex items-start gap-3 text-left">
                <Database className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <span className="text-sm text-foreground">
                  {t("noContactsStoredOnServers")}
                </span>
              </div>
            </div>
          )}

          {/* CTA buttons - platform-aware */}
          {showManualFlow ? (
            <div className="space-y-3 w-full max-w-xs">
              <Button className="w-full rounded-full h-12" onClick={onClose}>
                <UserPlus className="h-4 w-4 mr-2" />
                {t("addContactManually", { defaultValue: "Add Contact Manually" })}
              </Button>
              <Button
                variant="outline"
                className="w-full rounded-full h-12"
                onClick={() => {
                  onClose();
                  // Could link to app store in future
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                {t("getTheApp", { defaultValue: "Get the App" })}
              </Button>
              <button
                onClick={onClose}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("notNow")}
              </button>
            </div>
          ) : isDenied ? (
            <div className="space-y-3 w-full max-w-xs">
              {onOpenSettings && (
                <Button className="w-full rounded-full h-12" onClick={onOpenSettings}>
                  {t("openSettings")}
                </Button>
              )}
              <button
                onClick={onClose}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("searchByPhoneInstead")}
              </button>
            </div>
          ) : (
            <div className="space-y-3 w-full max-w-xs">
              <Button className="w-full rounded-full h-12" onClick={onAllow} disabled={isRequesting}>
                {isRequesting
                  ? t("requesting", { defaultValue: "Requesting…" })
                  : showWebPickerFlow
                  ? t("selectContacts", { defaultValue: "Select Contacts" })
                  : t("allowAccess")}
              </Button>
              <button
                onClick={onClose}
                disabled={isRequesting}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              >
                {t("notNow")}
              </button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
